import React, { useState, useMemo } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { ScrollArea } from '../../ui/scroll-area';
import {
  Eye,
  MousePointer2,
  ChevronLeft,
  ChevronRight,
  MonitorPlay,
  Grid3x3,
  Info,
  Copy,
  Check,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { VISUAL_MODULES } from './modules';
import ModuleCanvas from './ModuleCanvas';
import { groupOf } from './ZoneOverlay';
import { toast } from 'sonner';

const VisualTestLab = () => {
  const [moduleId, setModuleId] = useState(VISUAL_MODULES[0]?.id);
  const [mode, setMode] = useState('overview'); // 'overview' | 'drilldown'
  const [stepIdx, setStepIdx] = useState(0);
  const [selectedZone, setSelectedZone] = useState(null);
  const [detectedZones, setDetectedZones] = useState([]);
  const [disabledGroups, setDisabledGroups] = useState(new Set());
  const [copied, setCopied] = useState(false);

  const activeModule = useMemo(
    () => VISUAL_MODULES.find((m) => m.id === moduleId),
    [moduleId]
  );
  const steps = activeModule?.steps || [];
  const activeStep = mode === 'drilldown' ? steps[stepIdx] : null;

  // Group detected zones by prefix → counts, sorted by descending size.
  const groupCounts = useMemo(() => {
    const map = new Map();
    detectedZones.forEach((z) => {
      const g = groupOf(z.testid);
      map.set(g, (map.get(g) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([group, count]) => ({ group, count }))
      .sort((a, b) => b.count - a.count || a.group.localeCompare(b.group));
  }, [detectedZones]);

  const toggleGroup = (g) => {
    setDisabledGroups((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  };
  const enableAllGroups = () => setDisabledGroups(new Set());
  const disableAllGroups = () =>
    setDisabledGroups(new Set(groupCounts.map((g) => g.group)));

  const visibleZoneCount = useMemo(
    () => detectedZones.filter((z) => !disabledGroups.has(groupOf(z.testid))).length,
    [detectedZones, disabledGroups]
  );

  const copyTestid = (val) => {
    navigator.clipboard?.writeText(val);
    setCopied(true);
    toast.success(`Copied: ${val}`);
    setTimeout(() => setCopied(false), 1200);
  };

  const currentInfo = useMemo(() => {
    if (mode === 'drilldown' && activeStep) {
      const z = detectedZones.find(
        (x) => x.testid === activeStep.testid || x.testid === activeStep.fallbackTestid
      );
      return {
        title: activeStep.title,
        hint: activeStep.hint,
        testid: activeStep.testid,
        tag: z?.tag,
        text: z?.text,
        found: !!z,
      };
    }
    if (selectedZone) {
      return {
        title: selectedZone.testid,
        hint: selectedZone.text
          ? `Visible text: "${selectedZone.text}"`
          : 'No visible text content. Likely a container/layout element.',
        testid: selectedZone.testid,
        tag: selectedZone.tag,
        text: selectedZone.text,
        found: true,
      };
    }
    return null;
  }, [mode, activeStep, selectedZone, detectedZones]);

  return (
    <div
      className="grid grid-cols-12 gap-4 h-[calc(100vh-220px)]"
      data-testid="visual-test-lab"
    >
      {/* LEFT SIDEBAR — Module picker + drill-down steps */}
      <div className="col-span-3">
        <Card className="h-full flex flex-col">
          <div className="px-3 py-2.5 border-b border-border">
            <div className="flex items-center gap-1.5 mb-1">
              <MonitorPlay className="w-3.5 h-3.5 text-violet-500" />
              <h3 className="text-sm font-semibold">Visual Lab</h3>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Live iframe · auto-detected zones · click to inspect
            </p>
          </div>

          {/* Module picker */}
          <div className="px-3 py-2.5 border-b border-border">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              Module
            </label>
            <select
              value={moduleId}
              onChange={(e) => {
                setModuleId(e.target.value);
                setStepIdx(0);
                setSelectedZone(null);
              }}
              className="mt-1 w-full text-xs px-2 py-1.5 rounded border border-border bg-background"
              data-testid="visual-lab-module-picker"
            >
              {VISUAL_MODULES.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            {activeModule?.description && (
              <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
                {activeModule.description}
              </p>
            )}
          </div>

          {/* Mode toggle */}
          <div className="px-3 py-2.5 border-b border-border">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              Mode
            </label>
            <div className="mt-1 flex items-center rounded-md border border-border p-0.5 gap-0.5">
              <button
                className={cn(
                  'flex-1 text-[11px] px-2 py-1 rounded flex items-center justify-center gap-1 transition-colors',
                  mode === 'overview'
                    ? 'bg-violet-500 text-white'
                    : 'text-muted-foreground hover:bg-muted'
                )}
                onClick={() => setMode('overview')}
                data-testid="visual-lab-mode-overview"
              >
                <Grid3x3 className="w-3 h-3" /> Overview
              </button>
              <button
                className={cn(
                  'flex-1 text-[11px] px-2 py-1 rounded flex items-center justify-center gap-1 transition-colors',
                  mode === 'drilldown'
                    ? 'bg-violet-500 text-white'
                    : 'text-muted-foreground hover:bg-muted'
                )}
                onClick={() => setMode('drilldown')}
                data-testid="visual-lab-mode-drilldown"
              >
                <MousePointer2 className="w-3 h-3" /> Drill-down
              </button>
            </div>
          </div>

          {/* Drill-down step list */}
          {mode === 'drilldown' && steps.length > 0 && (
            <ScrollArea className="flex-1">
              <div className="px-2 py-2 space-y-1">
                {steps.map((s, i) => {
                  const active = i === stepIdx;
                  return (
                    <button
                      key={s.id}
                      onClick={() => {
                        setStepIdx(i);
                        setSelectedZone(null);
                      }}
                      className={cn(
                        'w-full text-left px-2.5 py-2 rounded-md border text-xs transition-colors',
                        active
                          ? 'border-violet-500 bg-violet-500/10'
                          : 'border-border hover:border-violet-500/50 hover:bg-muted/50'
                      )}
                      data-testid={`visual-lab-step-${s.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0',
                            active
                              ? 'bg-violet-500 text-white'
                              : 'bg-muted text-muted-foreground'
                          )}
                        >
                          {i + 1}
                        </span>
                        <span
                          className={cn(
                            'font-medium truncate',
                            active ? 'text-foreground' : 'text-foreground/80'
                          )}
                        >
                          {s.title}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          )}

          {/* Overview group toggles */}
          {mode === 'overview' && (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase font-semibold text-muted-foreground">
                    Visible zones
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-semibold tabular-nums">
                      {visibleZoneCount}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      / {detectedZones.length}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={enableAllGroups}
                    className="text-[10px] px-1.5 py-0.5 rounded border border-border hover:bg-muted"
                    data-testid="visual-lab-groups-all"
                    title="Show all groups"
                  >
                    all
                  </button>
                  <button
                    onClick={disableAllGroups}
                    className="text-[10px] px-1.5 py-0.5 rounded border border-border hover:bg-muted"
                    data-testid="visual-lab-groups-none"
                    title="Hide all groups"
                  >
                    none
                  </button>
                </div>
              </div>
              <ScrollArea className="flex-1">
                <div className="px-2 py-2 space-y-0.5">
                  {groupCounts.length === 0 && (
                    <p className="text-[10px] text-muted-foreground px-2 py-3 text-center italic">
                      Loading zones…
                    </p>
                  )}
                  {groupCounts.map(({ group, count }) => {
                    const disabled = disabledGroups.has(group);
                    return (
                      <button
                        key={group}
                        onClick={() => toggleGroup(group)}
                        className={cn(
                          'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-left transition-colors',
                          disabled
                            ? 'text-muted-foreground/60 hover:bg-muted/40'
                            : 'text-foreground hover:bg-violet-500/10'
                        )}
                        data-testid={`visual-lab-group-${group}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className={cn(
                              'w-2 h-2 rounded-full flex-shrink-0',
                              disabled ? 'bg-muted-foreground/30' : 'bg-violet-500'
                            )}
                          />
                          <code
                            className={cn(
                              'text-[10px] font-mono truncate',
                              disabled && 'line-through'
                            )}
                          >
                            {group}
                          </code>
                        </div>
                        <span className="text-[10px] text-muted-foreground tabular-nums flex-shrink-0">
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
              <div className="px-3 py-2 border-t border-border">
                <p className="text-[9px] text-muted-foreground leading-relaxed">
                  Click any group to hide/show its zones. Hover a zone on the canvas to see its <code className="bg-muted px-1 rounded">data-testid</code>.
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* CENTER — Canvas */}
      <div className="col-span-6">
        {activeModule ? (
          <ModuleCanvas
            module={activeModule}
            mode={mode}
            activeStep={activeStep}
            onZoneClick={(z) => setSelectedZone(z)}
            onZonesDetected={setDetectedZones}
            disabledGroups={disabledGroups}
          />
        ) : (
          <Card className="h-full flex items-center justify-center text-sm text-muted-foreground">
            Select a module
          </Card>
        )}
      </div>

      {/* RIGHT SIDEBAR — Metadata / zone details */}
      <div className="col-span-3">
        <Card className="h-full flex flex-col" data-testid="visual-lab-inspector">
          <div className="px-3 py-2.5 border-b border-border flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5 text-violet-500" />
            <h3 className="text-sm font-semibold">Inspector</h3>
          </div>

          {currentInfo ? (
            <ScrollArea className="flex-1">
              <div className="p-3 space-y-3 text-xs">
                <div>
                  <div className="text-[10px] uppercase font-semibold text-muted-foreground">
                    {mode === 'drilldown' ? `Step ${stepIdx + 1} of ${steps.length}` : 'Zone'}
                  </div>
                  <h4 className="text-sm font-semibold mt-0.5">{currentInfo.title}</h4>
                  {!currentInfo.found && mode === 'drilldown' && (
                    <Badge variant="outline" className="mt-1 text-[9px] text-amber-600 border-amber-500/50">
                      Not visible on screen
                    </Badge>
                  )}
                </div>

                {currentInfo.hint && (
                  <p className="text-[11px] leading-relaxed text-foreground/80">
                    {currentInfo.hint}
                  </p>
                )}

                <div className="space-y-1.5 pt-2 border-t border-border">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase font-semibold text-muted-foreground">
                      testid
                    </span>
                    <button
                      onClick={() => copyTestid(currentInfo.testid)}
                      className="text-[10px] text-violet-500 hover:text-violet-600 flex items-center gap-1"
                      data-testid="visual-lab-copy-testid"
                    >
                      {copied ? (
                        <>
                          <Check className="w-3 h-3" /> copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" /> copy
                        </>
                      )}
                    </button>
                  </div>
                  <code className="block bg-muted px-2 py-1 rounded text-[10px] font-mono break-all">
                    {currentInfo.testid}
                  </code>
                </div>

                {currentInfo.tag && (
                  <div className="space-y-1">
                    <span className="text-[10px] uppercase font-semibold text-muted-foreground">
                      Element
                    </span>
                    <code className="block bg-muted px-2 py-1 rounded text-[10px] font-mono">
                      &lt;{currentInfo.tag}&gt;
                    </code>
                  </div>
                )}

                {currentInfo.text && (
                  <div className="space-y-1">
                    <span className="text-[10px] uppercase font-semibold text-muted-foreground">
                      Visible text
                    </span>
                    <div className="bg-muted px-2 py-1 rounded text-[10px] italic">
                      "{currentInfo.text}"
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          ) : (
            <div className="flex-1 flex items-center justify-center p-4 text-center">
              <div className="text-[11px] text-muted-foreground space-y-2">
                <Eye className="w-6 h-6 mx-auto text-muted-foreground/60" />
                <p>
                  {mode === 'overview'
                    ? 'Hover and click any highlighted zone on the canvas to inspect it.'
                    : 'Pick a step from the sidebar to begin the guided tour.'}
                </p>
              </div>
            </div>
          )}

          {/* Drill-down controls */}
          {mode === 'drilldown' && steps.length > 0 && (
            <div className="px-3 py-2.5 border-t border-border flex items-center justify-between gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={stepIdx === 0}
                onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
                className="h-7 text-[11px] flex-1"
                data-testid="visual-lab-prev-step"
              >
                <ChevronLeft className="w-3 h-3 mr-1" /> Prev
              </Button>
              <Button
                size="sm"
                disabled={stepIdx >= steps.length - 1}
                onClick={() => setStepIdx((i) => Math.min(steps.length - 1, i + 1))}
                className="h-7 text-[11px] flex-1"
                data-testid="visual-lab-next-step"
              >
                Next <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default VisualTestLab;
